/*
 * Copyright (C) Serfim TIC
 * All rights reserved
 */
import { BaseController } from '../../BaseController';
import { Filter } from '../../../model/Filter';

class GuaranteedRestoreTimePolicyViewController extends BaseController {
    /** @ngInject */
    constructor(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log,
        guaranteedRestoreTimePolicyService, siteService, guaranteedRestoreTimeService, alarmTypeService,
        filterService, dialogService) {
        super(configuration, notificationService, waitSpinnerService, $q, $state, $stateParams, $log);
        this.guaranteedRestoreTimePolicyService = guaranteedRestoreTimePolicyService;
        this.siteService = siteService;
        this.guaranteedRestoreTimeService = guaranteedRestoreTimeService;
        this.alarmTypeService = alarmTypeService;
        this.filterService = filterService;
        this.dialogService = dialogService;
        this.$log.debug('Controller \'GuaranteedRestoreTimePolicyView\' loaded');
    }

    $onInit() {
        super.$onInit();

        this.waitSpinnerService.visible = true;
        this.formDisabled = false;
        this.guaranteedRestoreTimes = [];
        this.alarmTypes = [];
        this.grtByAlarmType = [];
        // Model bound to the view, which handles selected GRT for each type of alarm.
        this.alarmTypePolicies = [];

        // List of existing policies loaded for the selected site.
        const entity = {
            policies: []
        };
        if (this.$stateParams.hasOwnProperty('siteId')) {
            entity.siteId = this.$stateParams.siteId;
        }

        this.handleRestServiceCall(this.$q.all({
            entity,
            sites: this.siteService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            }),
            alarmTypes: this.alarmTypeService.findEntities({
                params: this.filterService.buildSortingParam({sort: 'name'})
            })
        }), data => {
            this.pristineEntity = data.entity;
            this.sites = data.sites;
            this.alarmTypes = data.alarmTypes;
            this.initPoliciesByAlarmType();
            this.reset();
        }).finally(() => {
            this.waitSpinnerService.visible = false;
        });
    }

    initPoliciesByAlarmType() {
        // Add the model for the 'default' alarm type.
        this.alarmTypePolicies.push({
            alarmTypeId: null,
            alarmTypeName: 'model.alarmtype.default'
        });
        // Add models for regular alarm types.
        this.alarmTypes.forEach(alarmType => {
            this.alarmTypePolicies.push({
                alarmTypeId: alarmType.id,
                alarmTypeName: alarmType.name
            });
        });
    }

    /**
     * Loads policies defined for a site.
     *
     * @param {Number} [siteId] Site ID.
     * @return {Promise} Promise containing the policies loaded, or an empty array if site ID is not provided.
     */
    loadPoliciesAndGrt(siteId) {
        let promise;
        if (siteId) {
            promise = this.handleRestServiceCall(
                this.getPolicies(siteId),
                policies => {
                    this.grtPolicy.policies = policies;
                    this.applyPolicies();
                }
            ).catch(() => {
                this.notificationService.pushMessage('ui.grtpolicy.policiesloadingfailed',
                    this.configuration.NOTIFIER_LEVEL_ERROR);
            });
        } else {
            promise = this.$q.resolve([]);
        }
        promise.then(() => this.getGrtBySite(siteId));
        return promise;
    }

    /**
     * Gets policies of a site.
     *
     * @param {Number} siteId Site ID.
     * @return {Promise} Promise containing the policies loaded.
     */
    getPolicies(siteId) {
        return this.guaranteedRestoreTimePolicyService.findEntities({
            data: this.filterService.buildFilters([Filter.equalFilter('site.id', siteId)])
        });
    }

    /**
     * Gets GRTs related to the domain of a given site.
     *
     * @param {Number} [siteId] Site ID.
     */
    getGrtBySite(siteId) {
        this.guaranteedRestoreTimes.splice(0);
        if (siteId) {
            this.siteService.getEntity(siteId).then(
                site => this.guaranteedRestoreTimeService.findEntities({
                    params: this.filterService.buildSortingParam({sort: 'name'}),
                    data: this.filterService.buildFilters([Filter.equalFilter('domain.id', site.domainId)])
                })
            ).then(guaranteedRestoreTimes => {
                guaranteedRestoreTimes.forEach(grt => this.guaranteedRestoreTimes.push(grt));
            });
        }
    }

    /**
     * Applies loaded policies to the model bound to the view.
     */
    applyPolicies() {
        this.alarmTypePolicies.forEach(alarmTypePolicy => {
            const policy = this.findGuaranteedRestoreTimePolicyByAlarmType(alarmTypePolicy.alarmTypeId);
            if (policy) {
                alarmTypePolicy.id = policy.id;
                alarmTypePolicy.version = policy.version;
                alarmTypePolicy.grtId = policy ? policy.grtId : undefined;
            } else {
                alarmTypePolicy.id = undefined;
                alarmTypePolicy.version = undefined;
                alarmTypePolicy.grtId = undefined;
            }
        });
    }

    /**
     * Finds the policy applied for a given alarm type, if existing, in the loaded policies.
     */
    findGuaranteedRestoreTimePolicyByAlarmType(alarmTypeId) {
        return this.grtPolicy.policies.find(policy => policy.alarmTypeId === alarmTypeId);
    }

    /**
     * Maps the list of policies to a list of DTO known by the server.
     *
     * @return {Array} Array of DTO.
     */
    mapToDto() {
        return this.alarmTypePolicies.filter(
            alarmTypePolicy => (alarmTypePolicy.grtId !== undefined) || (alarmTypePolicy.id !== undefined)
        ).map(
            alarmTypePolicy => {
                return {
                    id: alarmTypePolicy.id,
                    version: alarmTypePolicy.version,
                    siteId: this.grtPolicy.siteId,
                    alarmTypeId: alarmTypePolicy.alarmTypeId,
                    grtId: alarmTypePolicy.grtId
                };
            }
        );
    }

    save() {
        this.formDisabled = true;
        const dtoList = this.mapToDto();
        this.handleRestServiceCall(this.guaranteedRestoreTimePolicyService.updateEntities(dtoList), () => {
            this.notificationService.pushMessage('ui.grtpolicy.edited',
                this.configuration.NOTIFIER_LEVEL_INFO);
            if (this.updateMode) {
                this.$state.reload();
            } else {
                this.$state.go('gmao.organization.grtpolicy.list');
            }
        }).finally(() => {
            this.formDisabled = false;
        });
    }

    reset() {
        this.grtPolicy = angular.copy(this.pristineEntity);
        this.loadPoliciesAndGrt(this.grtPolicy.siteId);
    }
}

export const guaranteedRestoreTimePolicyViewComponent = {
    template: require('./guaranteedrestoretimepolicy-view.html'),
    controller: GuaranteedRestoreTimePolicyViewController
};



// WEBPACK FOOTER //
// ./src/main/front/scripts/module/organization/component/GuaranteedRestoreTimePolicyView.js